import Header from '@/components/Header';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { PushNotificationSettings } from '@/components/notifications/PushNotificationSettings';
import { Button } from '@/components/ui/button';
import { useNotificationActions, useNotifications } from '@/hooks/queries/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/api/error';
import { getNotificationLink, isNotificationUnread } from '@/lib/notifications';
import type { AppNotificationDto } from '@/types/dto/notification.dto';
import { Bell, CheckCheck, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, unreadCount, isError, isLoading, refetch } = useNotifications(user?.id, 100, Boolean(user));
  const { markRead, markAllRead, isMarkingAllRead } = useNotificationActions(user?.id);

  const openNotification = async (notification: AppNotificationDto) => {
    try {
      if (isNotificationUnread(notification)) {
        await markRead(notification.id);
      }

      const link = getNotificationLink(notification);

      if (link) {
        navigate(link);
      }
    } catch (error) {
      toast.error('Não foi possível atualizar a notificação', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      toast.success('Notificações marcadas como lidas');
    } catch (error) {
      toast.error('Não foi possível marcar as notificações', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  return (
    <div className="bg-muted/30 min-h-screen">
      <Header showBack title="Notificações" />
      <main className="container max-w-3xl py-6 sm:py-10">
        <PushNotificationSettings />
        <div className="mb-5 flex min-h-11 flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-foreground text-lg font-semibold">Suas atualizações</h2>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {unreadCount
                ? `${unreadCount} ${unreadCount === 1 ? 'notificação não lida' : 'notificações não lidas'}`
                : 'Você está em dia'}
            </p>
          </div>
          {unreadCount > 0 ? (
            <Button variant="outline" size="sm" onClick={() => void handleMarkAllRead()} disabled={isMarkingAllRead}>
              {isMarkingAllRead ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
              <span className="hidden sm:inline">Marcar todas como lidas</span>
              <span className="sm:hidden">Marcar lidas</span>
            </Button>
          ) : null}
        </div>

        {isLoading ? (
          <div className="text-muted-foreground flex min-h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" aria-label="Carregando notificações" />
          </div>
        ) : isError ? (
          <div className="bg-card rounded-2xl border px-6 py-12 text-center">
            <RefreshCw className="text-muted-foreground mx-auto h-8 w-8" aria-hidden="true" />
            <h2 className="mt-4 font-semibold">Não foi possível carregar</h2>
            <p className="text-muted-foreground mt-1 text-sm">Verifique sua conexão e tente novamente.</p>
            <Button className="mt-5" variant="outline" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-card rounded-2xl border px-6 py-14 text-center">
            <span className="bg-primary/10 text-primary mx-auto flex h-14 w-14 items-center justify-center rounded-2xl">
              <Bell className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 font-semibold">Nenhuma notificação</h2>
            <p className="text-muted-foreground mx-auto mt-2 max-w-sm text-sm">
              Confirmações de agendamento e outras novidades aparecerão aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} onOpen={openNotification} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
